import React, {useEffect, useMemo, useState} from 'react';
import {connect} from "react-redux";
import { Grid } from 'react-virtualized';
import 'react-virtualized/styles.css';
import {injectIntl} from 'react-intl';
import PropTypes from "prop-types";
import uniqBy from "lodash/uniqBy";

import {getCurrentLayout} from "../../../../../../../appLayout";
import {featureInterface} from "../../../../../../../../interfaces/featureInterface";
import SqftFeaturesChecker from "./SqftFeaturesChecker";
import SqftOptionsLoading from "./SqftOptionsLoading";
import SqftOptionsRelatedItemRow from "./SqftOptionsRelatedItemRow";
import {
  getFilteredRelatedFeatures,
  getRelatedFeaturesFromLayout,
} from "../../../../../../../../misc/helpers";
import {getSortedFeatures} from "../../../../../../helpers/featureHelper";
import {loaderDisable, loaderEnable} from "../../../../../../../ui/loaderView";
import {getGridColumnDimensions, useGridDimensions} from "../../../commonTools/featuresGrid/gridDimensions";

const SqftOptionsRelatedItemList = (props) => {
  const { windowHeight, isMobile, activeFeature, itemClickCallback, partId, shouldSortByWidth, searchInputValue,
    reCacheMenuCounter, lookViewType, selectedFilters, groheFiltersSearchText } = props;
  const [activeItemIndex, setActiveItemIndex] = React.useState(0);

  const [loading, setLoading] = useState(true);
  const [parentFeature, setParentFeature] = useState(null);

  const {gridWidth, gridHeight} = useGridDimensions(windowHeight, isMobile);

  const layout = useMemo(() => getCurrentLayout(), []);
  const featureGroup = useMemo(() => layout.getFeatureGroupByPartId(partId), [layout, partId]);

  useEffect(() => {
    if (featureGroup) {
      setLoading(!featureGroup.allFeaturesReceived);
      if (!featureGroup.allFeaturesReceived) {
        loaderEnable();
      }
    } else {
      setLoading(false);
    }
  }, [featureGroup]);

  useEffect(() => {
    if (!featureGroup) {
      setParentFeature(null);
      return;
    }

    let parentFeature;

    if (featureGroup.activeFeature) {
      parentFeature = featureGroup.activeFeature;
    } else if (!featureGroup.allFeaturesReceived) {
      setParentFeature(null);
      return;
    }

    if (!featureGroup.activeFeature) {
      //featureGroup.setDefault(layout, true);
      parentFeature = featureGroup.activeFeature ? featureGroup.activeFeature : featureGroup.activeNotInVisualizerFeature;
    }

    setParentFeature(parentFeature);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureGroup, layout, activeFeature, partId, loading, reCacheMenuCounter]);

  if (!featureGroup) {
    return <SqftOptionsLoading windowHeight={windowHeight} isMobile={isMobile} />
  }

  if (loading) {
    return (
      <SqftFeaturesChecker windowHeight={windowHeight} isMobile={isMobile} subPart={featureGroup} callBack={() => {
        if (featureGroup.allFeaturesReceived) {
          setLoading(false);
          loaderDisable();
        }
      }} />
    )
  }

  if (!parentFeature) {
    console.log("selected feature matched by name not found", partId);
    console.log("parentFeature", parentFeature);
    return null;
  }

  const relatedFeatures = parentFeature.getFeatureRelatedFeatures();

  if (!Array.isArray(relatedFeatures) || !relatedFeatures.length) {
    console.log("relatedFeatures are missing", relatedFeatures);
    console.log("parentFeature", parentFeature);
    return null;
  }

  // Helper function to replace abbreviations in search text
  const expandSearchText = (text) => {
    return text
      .replace(/\bh(\d)/gi, 'Height $1')
      .replace(/\bw(\d)/gi, 'Width $1')
      .replace(/\bd(\d)/gi, 'Depth $1')
      .replace(/\bh\b/gi, 'Height')
      .replace(/\bw\b/gi, 'Width')
      .replace(/\bd\b/gi, 'Depth');
  };
  const expandedGroheFiltersSearchText = expandSearchText(groheFiltersSearchText);

  //Now let's search for the related features
  const {noFeaturesFound, relatedFeaturesFound} = getRelatedFeaturesFromLayout(
    relatedFeatures,
    parentFeature,
    expandedGroheFiltersSearchText,
    selectedFilters
  );
  if (noFeaturesFound.length) {
    console.log("ERROR: noFeaturesFound", noFeaturesFound);
  }

  let relatedItemsList = getFilteredRelatedFeatures(relatedFeaturesFound);
  relatedItemsList = uniqBy(relatedItemsList, (feature) => feature.dbFeature.id);
  let emptyUrlsLog = [];

  relatedItemsList = relatedItemsList.filter(feature => {
    if (!feature.dbFeature.thirdPartyAR3DUrls) {
      //todo we can remove this searchInputValue and search by filters
      if (searchInputValue && feature.getFeatureName().toLowerCase().includes(searchInputValue.toLowerCase())
        || !searchInputValue
      ) {
        emptyUrlsLog.push(feature.dbFeature.sharedFeatureName);
      }
    } else {
      return feature;
    }
  });

  // const relatedGroups = createRelatedGroups(relatedItemsList, [], activeQuoteIndex, intl, fabricatorData)
  // void relatedGroups
  // console.log("relatedGroups", relatedGroups);
  //
  // const groupName = parentFeature.dbFeature.groupName;
  // void groupName;

  if (emptyUrlsLog.length > 0) {
    console.log(`Features with empty thirdPartyAR3DUrls: ${emptyUrlsLog.join(', ')}`);
  }

  if (shouldSortByWidth) {
    relatedItemsList = getSortedFeatures(relatedItemsList);
  }

  const {columnCount, rowHeight} = getGridColumnDimensions(lookViewType);

  const columnWidth = Math.floor(gridWidth / columnCount) - 8 / columnCount; // Ensure width is an integer to avoid overflow
  const rowCount = Math.ceil(relatedItemsList.length / columnCount);

  return (
    <div className="side-level2__content">
      {
        <Grid
          width={gridWidth}
          height={gridHeight}
          columnCount={columnCount}
          columnWidth={columnWidth}
          rowCount={rowCount}
          rowHeight={rowHeight}
          cellRenderer={({ columnIndex, key, rowIndex, style }) => {
            const index = rowIndex * columnCount + columnIndex;
            if (index >= relatedItemsList.length) return null;
            return (
              <SqftOptionsRelatedItemRow
                key={key}
                style={style}
                partId={partId}
                feature={relatedItemsList[index]}
                parentFeature={parentFeature}
                activeItemIndex={activeItemIndex}
                setActiveItemIndex={setActiveItemIndex}
                itemClickCallback={itemClickCallback}
              />
            )
          }}
        />
      }
    </div>
  )
};

SqftOptionsRelatedItemList.propTypes = {
  activeFeature: PropTypes.shape(featureInterface),
  partId: PropTypes.string,
  shouldSortByWidth: PropTypes.bool,
  itemClickCallback: PropTypes.func,
  //searchInputValue: PropTypes.string
};

const mapStateToProps = (store) => {
  const { menuOpened, reCacheMenuCounter } = store.sqftMenuState;
  const { visualizersLoaded } = store.serverState;
  const { windowHeight, isMobile } = store.serverState;
  const { activeQuoteIndex } = store.quotesState;
  const { sqftFromFeature } = store.sqftState;
  const { lookViewType } = store.lookState;
  const {groheFiltersSearchText} = store.groheState;
  const {selectedFilters} = store.filterState;

  return {
    menuOpened,
    windowHeight,
    isMobile,
    activeQuoteIndex,
    visualizersLoadedLength: visualizersLoaded.length,
    fabricatorData: store.fabricatorState,
    sqftFromFeature,
    reCacheMenuCounter,
    groheFiltersSearchText,
    selectedFilters,
    lookViewType,
  };
};

const mapDispatchToProps = {
};
export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(SqftOptionsRelatedItemList)
)
